import { Area, AreaChart, YAxis } from "recharts";
import { ChartContainer } from "@ds/ui/chart";
import { bounds } from "../movements.chart";

/**
 * THE ESTIMATE, AS A THUMBNAIL — the shape of a lift, in the width a row can spare.
 *
 * ═══ WHY IT IS THE SAME CHART LIBRARY AND NOT A HAND-DRAWN SVG ═══
 *
 * `MovementTrend`'s header records why four hand-rolled SVG charts were replaced: each one knew
 * its own way to map a value to a y-coordinate, and four of them could not agree. None of the
 * reasons they were replaced FOR applies at this size — there is no axis, no tick, no tooltip
 * and no text inside the plot — but the mapping is the same mapping, and `bounds` is shared with
 * the full-size chart so the two cannot disagree about what "flat" looks like.
 *
 * ═══ 🚨 WHAT IT DELIBERATELY HAS NONE OF ═══
 *
 * No axis, no grid, no tooltip, no reference line, no dots. It is a SHAPE, not a chart somebody
 * reads a value off — the figure beside it is the number, and it is exact. A tooltip here would
 * also be a tap target on a screen whose rule is that nothing tappable changes anything
 * (BUD-388), which is a second reason and a better one.
 *
 * ⚠️ IT IS `aria-hidden` AND CARRIES NO LABEL. The full-size chart names itself to a screen
 * reader because it is the only place the series exists; this one restates what the figure and
 * the "last time" line beside it already say in words. A description of a decoration is noise
 * in a screen reader, and the drawer underneath holds the real thing.
 */
export function MovementSparkline({
  series,
  className,
}: {
  /** Oldest first, one entry per bucket. A null is a bucket that produced no estimate. */
  series: readonly { periodStart: string; value: number | null }[];
  className?: string;
}) {
  /* A LEVEL, SO THE WINDOW HUGS THE DATA. Nobody's squat goes to zero, and a zero-based
     baseline squeezes a year of real progress into the top quarter of a 28px box — which at
     this size is a flat line. Same call, same function, as the chart this is a thumbnail of. */
  const window = bounds(
    series.map((row) => row.value).filter((value): value is number => value !== null),
    false,
  );

  return (
    <ChartContainer
      config={{ value: { color: "var(--color-accent)" } }}
      className={className}
      data-testid="movement-sparkline"
      aria-hidden
    >
      <AreaChart data={[...series]} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
        {/* 🔑 HIDDEN, BUT NOT ABSENT. Without it Recharts fits its own vertical window per
            chart, and the shared `bounds` above — the whole reason this agrees with the
            full-size chart about what "flat" looks like — would never reach the plot. */}
        <YAxis hide domain={[window.min, window.max]} />
        <defs>
          <linearGradient id="movement-sparkline-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-accent)" stopOpacity={0.25} />
            <stop offset="100%" stopColor="var(--color-accent)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          dataKey="value"
          type="linear"
          stroke="var(--color-accent)"
          strokeWidth={1.5}
          fill="url(#movement-sparkline-fill)"
          /* 🚨 FALSE, AND IT IS THE ONE PROP ON THIS CHART THAT IS ABOUT HONESTY. A bucket with
             no measurable set has no value, and joining across it would draw a progression
             through weeks that have no such number in them. */
          connectNulls={false}
          dot={false}
          /* The fill runs down to the floor of the window rather than to zero, which is where
             the hidden axis above puts the bottom of the box. */
          baseValue={window.min}
          /* 🚨 NO ENTRY ANIMATION. Recharts grows a line's dash offset from zero on mount; this
             row appears inside a scroll area an athlete flicks past between sets, and a
             thumbnail that assembles itself over a second and a half is a thumbnail nobody
             sees. `MovementTrend` turns it off for the same reason at full size. */
          isAnimationActive={false}
        />
      </AreaChart>
    </ChartContainer>
  );
}
